import { virtualInput } from './virtualInput.js';
import { isInputLocked } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { FLIP_LAYERS } from './NatalkaSprite.js';
import { feetForCentre } from './outdoorScale.mjs';
import { bikeSession } from './bikeSession.js';
import { altanaForLevel, seatCentre } from '../scenes/backyardLayout.js';
import { nearestFreeSeat, seatsTakenBy, SIT_RADIUS, CLICK_SIT_RANGE } from './altanaSeatsLogic.js';

const SIT_LABEL = 'Usiądź';
const STAND_LABEL = 'Wstań';

// Same check WalkController uses (its helper is module-private): don't treat typing in the
// chat / friend-name field as "movement".
function isTextInputFocused() {
  const tag = document.activeElement?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA';
}

// Altana seating for one scene (owner or visit). Offers "Usiądź" when the feet are within
// SIT_RADIUS of a free seat (proximity action button, like the Game Hall) and makes each
// seat clickable within CLICK_SIT_RANGE. Sitting = walker off, avatar snapped to the anchor,
// presence broadcast {mode:'sit', seat}. Any movement input, the "Wstań" action, or a click
// on another free seat stands you up / moves you. Seat claims are soft: a seat that a peer
// is currently seen sitting on is simply not offered.
export class AltanaSeats {
  constructor(scene, { natalka, walker, getLevel, getPeers, onBroadcast }) {
    this.scene = scene;
    this.natalka = natalka;
    this.walker = walker;
    this._getLevel = getLevel;
    this._getPeers = getPeers;          // () => iterable of { mode, seat }
    this._broadcast = onBroadcast;      // (payload) => void
    this.mySeat = null;
    this._offerSeat = null;
    this._zones = [];
    this._level = null;
    this._cursors = scene.input.keyboard.createCursorKeys();
    this._wasd = scene.input.keyboard.addKeys({ W: 'W', A: 'A', S: 'S', D: 'D' });
    this._rebuild();
    scene.events.once('shutdown', () => this.destroy());
  }

  get isSeated() { return this.mySeat !== null; }
  get altana() { return altanaForLevel(this._getLevel()); }

  // Container centre for a seated avatar on this seat. Seated avatars are cropped to their
  // top half, so the centre sits ON the seat's sit line (see seatCentre). NOTE: proximity
  // and click tests still use altana.seats — those stay FEET coordinates.
  seatAnchorCentre(seat) {
    return seatCentre(this.altana, seat);
  }

  // Re-create the clickable seat zones when the level (and so the anchors) changes.
  _rebuild() {
    const lvl = this._getLevel();
    if (lvl === this._level) return;
    this._level = lvl;
    for (const z of this._zones) z.destroy();
    this._zones = [];
    if (this.mySeat !== null) this.stand();
    const a = this.altana;
    if (!a) return;
    a.seats.forEach((s, i) => {
      const zone = this.scene.add.zone(s.x, s.y - 40, 70, 80).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => this._onSeatClick(i));
      this._zones.push(zone);
    });
  }

  _taken() { return seatsTakenBy(this._getPeers?.() ?? []); }

  // Mirror (or un-mirror) the avatar's body layers — same set RemoteAvatar uses, so
  // 'accessory_back' is left alone.
  _setFacing(left) {
    for (const key of FLIP_LAYERS) this.natalka.layers?.[key]?.setFlipX?.(left);
  }

  _onSeatClick(i) {
    if (isInputLocked(this.scene)) return;
    if (bikeSession.riding) return;   // riding: the seat belongs to the bike, not the bench
    if (this._taken().has(i) || this.mySeat === i) return;
    const fx = this.natalka.x, fy = feetForCentre(this.scene, this.natalka.y);
    const s = this.altana.seats[i];
    if (Math.hypot(fx - s.x, fy - s.y) > CLICK_SIT_RANGE && this.mySeat === null) return;
    this.sit(i);
  }

  sit(i) {
    const a = this.altana;
    if (!a || !a.seats[i]) return;
    this.mySeat = i;
    this.walker.enabled = false;
    const c = this.seatAnchorCentre(i);
    this.natalka.setPosition(c.x, c.y);
    this.natalka.setSeated(true);
    const facing = a.facing?.[i] ?? 'right';
    this._setFacing(facing === 'left');
    audioManager.playSfx('click');
    this._broadcast?.({ x: c.x, y: c.y, facing, mode: 'sit', seat: i });
    virtualInput.registerAction(STAND_LABEL, () => this.stand());
  }

  stand() {
    if (this.mySeat === null) return;
    this.mySeat = null;
    this.walker.enabled = true;
    this.natalka.setSeated(false);
    // sit() mirrored her for a left-facing seat; the walker only flips on movement, so
    // without this she'd walk away mirrored until the scene restarted.
    this._setFacing(false);
    this._broadcast?.({ x: this.natalka.x, y: this.natalka.y, facing: 'right', mode: 'walk' });
    const cur = virtualInput.getAction();
    if (cur?.label === STAND_LABEL) virtualInput.clearAction();
  }

  _movementPressed() {
    const c = this._cursors, k = this._wasd, v = virtualInput.vector;
    return c.left.isDown || c.right.isDown || c.up.isDown || c.down.isDown
      || k.A.isDown || k.D.isDown || k.W.isDown || k.S.isDown
      || (v && (v.x !== 0 || v.y !== 0));
  }

  update() {
    this._rebuild();
    if (!this.altana) return;
    // On a bike the action slot belongs to BikeController ('rower'); both writing to it
    // every frame made the mobile action button flicker and let a tap mount the bike
    // while still seated.
    if (bikeSession.riding) return;
    if (isInputLocked(this.scene)) {
      const a = virtualInput.getAction();
      if (a && (a.label === SIT_LABEL || a.label === STAND_LABEL)) virtualInput.clearAction();
      return;
    }
    if (this.mySeat !== null) {
      if (!isTextInputFocused() && this._movementPressed()) { this.stand(); return; }
      if (virtualInput.getAction()?.label !== STAND_LABEL) virtualInput.registerAction(STAND_LABEL, () => this.stand());
      return;
    }
    const fx = this.natalka.x, fy = feetForCentre(this.scene, this.natalka.y);
    const near = nearestFreeSeat(fx, fy, this.altana.seats, this._taken(), SIT_RADIUS);
    const cur = virtualInput.getAction();
    if (near !== null) {
      if (cur?.label !== SIT_LABEL || this._offerSeat !== near) {
        this._offerSeat = near;
        virtualInput.registerAction(SIT_LABEL, () => this.sit(near));
      }
    } else if (cur && (cur.label === SIT_LABEL || cur.label === STAND_LABEL)) {
      this._offerSeat = null;
      virtualInput.clearAction();
    }
  }

  destroy() {
    for (const z of this._zones) z.destroy();
    this._zones = [];
    const cur = virtualInput.getAction();
    if (cur && (cur.label === SIT_LABEL || cur.label === STAND_LABEL)) virtualInput.clearAction();
  }
}
